// ─── Roles RBAC ────────────────────────────────────────────────────────────────

export type Role =
  'platform_owner' | 'restaurant_owner' | 'manager' | 'kitchen' | 'waiter' | 'customer'

/**
 * Gerente (#284, decisão de 14/10/2026): faz TUDO que o dono faz no
 * restaurante (dashboard, cardápio, mesas, equipe, configurações, impostos,
 * traduções, marca, operação) EXCETO trocar de plano e cancelar a assinatura.
 * Na equipe, o Gerente não convida/promove ninguém a Dono e não desativa nem
 * reativa um Dono (ver routes/admin/users.ts).
 */
export const ADMIN_ROLES = ['manager', 'restaurant_owner', 'platform_owner'] as const

/** Plano/assinatura (checkout, portal, cancelamento): só o dono. */
export const BILLING_ROLES = ['restaurant_owner', 'platform_owner'] as const

/** Operação de salão/cozinha (hoje igual a ADMIN_ROLES). */
export const OPERATIONS_ROLES = ['manager', 'restaurant_owner', 'platform_owner'] as const

/** Quem aprova/recusa pedidos de ajuste de preço e aplica direto (#274/#284). */
export const ADJUSTMENT_APPROVER_ROLES = ['manager', 'restaurant_owner', 'platform_owner'] as const

export function isAdjustmentApprover(role: string | undefined | null): boolean {
  return !!role && (ADJUSTMENT_APPROVER_ROLES as readonly string[]).includes(role)
}

// ─── JWT Claims ────────────────────────────────────────────────────────────────

export type JwtPayload = {
  sub: string
  role: Role
  tenant_id: string
  jti: string
  iat: number
  exp: number
  impersonating?: {
    tenant_id: string
    /** Ausente quando o restaurante ainda não tem dono ativo (#395). */
    restaurant_owner_id?: string | null
  }
  /**
   * Todas as roles de staff da pessoa no tenant (#284: multi-role — ex.:
   * Gerente + Garçom). Permissões = UNIÃO destas roles. Ausente em tokens
   * antigos/cliente → só `role`.
   */
  roles?: Role[]
}

/** Prioridade (maior primeiro) — define a role "principal"/tela inicial. */
export const ROLE_PRIORITY: readonly Role[] = [
  'platform_owner',
  'restaurant_owner',
  'manager',
  'waiter',
  'kitchen',
  'customer',
]

/** União das roles do token (role do contexto + roles de staff). */
export function rolesOf(jwt: Pick<JwtPayload, 'role' | 'roles'> | null | undefined): Role[] {
  if (!jwt) return []
  if (jwt.role === 'customer') return ['customer'] // contexto cliente não herda staff
  return [...new Set<Role>([jwt.role, ...(jwt.roles ?? [])])]
}

/** Role mais alta da pessoa (ator em auditoria/regras de status). */
export function highestRole(jwt: Pick<JwtPayload, 'role' | 'roles'>): Role {
  const all = rolesOf(jwt)
  return ROLE_PRIORITY.find((r) => all.includes(r)) ?? jwt.role
}

export function hasAnyRole(
  jwt: Pick<JwtPayload, 'role' | 'roles'> | null | undefined,
  roles: readonly string[],
): boolean {
  return rolesOf(jwt).some((r) => roles.includes(r))
}

// ─── Order ─────────────────────────────────────────────────────────────────────

export type OrderChannel = 'dine_in' | 'takeaway' | 'delivery'

export type OrderStatus =
  | 'pending'
  | 'confirmed'
  | 'preparing'
  | 'ready'
  | 'delivered'
  | 'cancelled'
  /** Fluxo garçom (order_flow='waiter', 0085): o pedido termina no garçom,
   *  que o relança no sistema local do restaurante — sem cozinha/kanban. */
  | 'waiter_sent'
  | 'waiter_ack'
  | 'relayed'

// "Conta paga" não é um status: é representada pela coluna `paid_at` em orders
// (pagamento no caixa/garçom é ortogonal ao fluxo de preparo).

/** Fluxo de recepção dos pedidos da mesa (0085): 'full' = pipeline completo
 *  (cozinha + salão, atual); 'waiter' = modo garçom — o cliente envia o pedido
 *  ao garçom, que lança no POS local do restaurante. */
export type OrderFlow = 'full' | 'waiter'

export type OrderItemStatus = 'pending' | 'preparing' | 'done'

// ─── Payment ───────────────────────────────────────────────────────────────────

export type PaymentStatus = 'pending' | 'processing' | 'succeeded' | 'failed' | 'refunded'

export type SplitType = 'percent' | 'fixed' | 'by_items'

export type GatewaySlug =
  | 'stripe'
  | 'pix'
  | 'pag_seguro'
  | 'mb_way'
  | 'multibanco'
  | 'ideal'
  | 'swish'
  | 'vipps'
  | 'razorpay'

// ─── Translation ───────────────────────────────────────────────────────────────

export type TranslationStatus = 'manual' | 'ai_draft' | 'published'

// ─── Real-time Events (WebSocket) ──────────────────────────────────────────────

export type WsEventType =
  | 'order.created'
  | 'order.confirmed'
  | 'order.preparing'
  | 'order.item.done'
  | 'order.ready'
  | 'order.delivered'
  | 'order.cancelled'
  | 'order.paid'
  | 'order.reordered'
  | 'order.updated'
  | 'order.transferred'
  | 'table.call_waiter'
  | 'waiter_call.attended'
  | 'order.waiter_ack'
  | 'order.relayed'
  | 'bill.updated'
  | 'bill.payment.updated'
  | 'adjustment.requested'
  | 'adjustment.decided'
  /** #132: mesa A unida na mesa B — o salão re-agrupa os pedidos */
  | 'table.merged'

export type WsEvent = {
  type: WsEventType
  tenant_id: string
  payload: Record<string, unknown>
  timestamp: number
}

// ─── API Responses ─────────────────────────────────────────────────────────────

export type ApiSuccess<T> = {
  ok: true
  data: T
}

export type ApiError = {
  ok: false
  error: {
    code: string
    message: string
    details?: unknown
  }
}

export type ApiResponse<T> = ApiSuccess<T> | ApiError

// ─── Realtime audience (#275) ──────────────────────────────────────────────────
// Quem pode receber cada evento. A API diz QUAIS clientes (customer_id) são
// donos do pedido; as roles de staff são fixas por tipo de evento e aplicadas
// pelo Durable Object — nunca pelo cliente.

/** Roles de staff que conectam ao tempo real. */
export type RealtimeStaffRole =
  'kitchen' | 'waiter' | 'manager' | 'restaurant_owner' | 'platform_owner'
export type RealtimeRole = RealtimeStaffRole | 'customer'

export const REALTIME_ROLES: readonly RealtimeRole[] = [
  'kitchen',
  'waiter',
  'manager',
  'restaurant_owner',
  'platform_owner',
  'customer',
]

const OWNERS: RealtimeStaffRole[] = ['restaurant_owner', 'platform_owner']
/** Gerente recebe o que o dono recebe na operação (#284). */
const MANAGERS: RealtimeStaffRole[] = ['manager', ...OWNERS]
const ALL_STAFF: RealtimeStaffRole[] = ['kitchen', 'waiter', ...MANAGERS]
const FLOOR: RealtimeStaffRole[] = ['waiter', ...MANAGERS]

/** Allowlist de staff por evento. Evento desconhecido → donos e Gerente (#284). */
export const WS_EVENT_STAFF_ROLES: Record<WsEventType, readonly RealtimeStaffRole[]> = {
  'order.created': ALL_STAFF,
  'order.confirmed': ALL_STAFF,
  'order.updated': ALL_STAFF,
  'order.preparing': ALL_STAFF,
  'order.ready': ALL_STAFF,
  'order.delivered': ALL_STAFF,
  'order.cancelled': ALL_STAFF,
  'order.item.done': ALL_STAFF,
  'order.reordered': ALL_STAFF,
  'order.transferred': ALL_STAFF,
  'order.paid': FLOOR,
  'table.call_waiter': FLOOR,
  'waiter_call.attended': FLOOR,
  // fluxo garçom (0085): salão acompanha recebimento e lançamento no local
  'order.waiter_ack': FLOOR,
  'order.relayed': FLOOR,
  'bill.updated': FLOOR,
  // #163: parte paga online / reembolsada — o salão acompanha em tempo real
  'bill.payment.updated': FLOOR,
  // pedido de ajuste (#274/#284): aprovadores recebem para a caixa de
  // aprovação; o salão vê o estado do próprio pedido
  'adjustment.requested': FLOOR,
  'adjustment.decided': FLOOR,
  // #132: união de mesas — salão reagrupa; cozinha não re-agrupa por mesa
  'table.merged': FLOOR,
}

/** Eventos que um cliente pode receber — e só sobre o próprio pedido. */
export const WS_CUSTOMER_EVENTS: ReadonlySet<WsEventType> = new Set<WsEventType>([
  'order.created',
  'order.confirmed',
  'order.updated',
  'order.preparing',
  'order.ready',
  'order.delivered',
  'order.cancelled',
  'order.item.done',
  'order.paid',
  'order.transferred',
  'order.waiter_ack',
  'order.relayed',
])

/** Público-alvo enviado pela API junto do evento (binding interno). */
export type WsAudience = {
  /** customer_id dos donos do pedido; ausente/vazio → nenhum cliente recebe */
  customerIds?: string[]
}

/** Envelope do POST /broadcast API → DO. */
export type WsBroadcast = { event: WsEvent; audience?: WsAudience }

/** Campos que nunca vão para clientes (metadado interno de staff). */
const CUSTOMER_STRIPPED_FIELDS = ['by_role', 'by', 'from_table_id'] as const

export type WsRecipient = { role: string; userId: string }

/**
 * Decide se `recipient` recebe `event` e com qual payload.
 * Retorna null quando não pode receber (default seguro).
 */
export function scopeEventFor(
  recipient: WsRecipient,
  event: WsEvent,
  audience: WsAudience | undefined,
): WsEvent | null {
  if (recipient.role === 'customer') {
    if (!WS_CUSTOMER_EVENTS.has(event.type)) return null
    if (!audience?.customerIds?.includes(recipient.userId)) return null
    const payload: Record<string, unknown> = { ...event.payload }
    for (const f of CUSTOMER_STRIPPED_FIELDS) delete payload[f]
    return { ...event, payload }
  }
  const allowed = WS_EVENT_STAFF_ROLES[event.type] ?? MANAGERS
  return (allowed as readonly string[]).includes(recipient.role) ? event : null
}
